import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  AreaChart,
} from "recharts";
import { DarkGrey, Primary, White } from "src/styles/color";
import { convertIntToCurrency } from "src/common";
import useMediaQuery from "@mui/material/useMediaQuery";
import device from "src/styles/device";
import font from "src/styles/font";

// const CustomTooltip = ({ active, payload }) => {
//   if (active && payload && payload.length) {
//     return (
//       <Box
//         sx={{
//           backgroundColor: Primary,
//           color: White,
//           padding: "4px 10px",
//           borderRadius: "20px",
//           fontSize: 14,
//         }}
//       >
//         ฿ {convertIntToCurrency(payload[0].value)}
//       </Box>
//     );
//   }
//   return null;
// };

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: White,
          color: Primary,
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: 14,
          border: `1px solid ${Primary}`,
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          zIndex: 100,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: 12,
            fontWeight: 400,
            color: Primary,
          }}
        >
          {payload[0].payload._id}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: Primary,
          }}
        >
          ฿ {convertIntToCurrency(payload[0].value)}
        </Typography>
      </Box>
    );
  }
  return null;
};

const CustomXAxisTick = ({ x, y, payload }) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        fontSize={font.size.sm}
        fontWeight={500}
        fill="#666"
        textAnchor="middle"
        // transform="rotate(-45)"
      >
        {payload.value}
      </text>
    </g>
  );
};

const getDivision = (value) => {
  if (value < 10000) {
    return 1000;
  } else if (value < 100000) {
    return 10000;
  } else if (value < 1000000) {
    return 100000;
  } else {
    return 1000000;
  }
};

const findNearestMaxValue = (data) => {
  const maxValue = Math.max(...data.map((item) => item.value));
  const division = getDivision(maxValue);
  const nearestMaxValue = Math.ceil(maxValue / division) * division;
  return nearestMaxValue;
};

const SalesChart = ({ data, selectedSeller, selectedCategory }) => {
  const nearestMaxValue = findNearestMaxValue(data);
  const yAxisDomain = [0, nearestMaxValue];
  const isSmallerThanMobileL = useMediaQuery(device.mobileL);
  const isSmallerThanTabletL = useMediaQuery(device.tabletL);
  const isSmallerThanDesktop = useMediaQuery(device.desktop);

  const getXInterval = (data) => {
    if (isSmallerThanMobileL) {
      return data.length > 4 ? Math.floor(data.length / 4) : 1;
    } else if (isSmallerThanTabletL) {
      return data.length > 6 ? Math.floor(data.length / 6) : 1;
    } else if (isSmallerThanDesktop) {
      return data.length > 8 ? Math.floor(data.length / 8) : 1;
    }
    return data.length > 10 ? Math.floor(data.length / 10) : 1;
  };

  const xInterval = getXInterval(data);

  return (
    <Box
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        background: White,
        // padding: 4,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" fontWeight="bold">
          {selectedCategory
            ? "ยอดขายสินค้า"
            : selectedSeller
              ? "ยอดขายร้านค้า"
              : "ยอดขายรวม"}{" "}
          <span
            style={{
              fontSize: "inherit",
              fontWeight: 400,
            }}
          >
            {selectedCategory
              ? selectedCategory.cateTh
              : selectedSeller
                ? selectedSeller.brandName
                : ""}
          </span>
        </Typography>
        {/* <Typography
          variant="body1"
          sx={{ color: Primary, fontWeight: "semibold" }}
        >
          ค่าเริ่มต้น
        </Typography> */}
      </Box>

      {data.length !== 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            data={data}
            margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
          >
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={Primary} stopOpacity={0.3} />
                <stop offset="100%" stopColor={Primary} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="_id"
              tick={<CustomXAxisTick />}
              interval={xInterval}
            />
            <YAxis
              dataKey="value"
              tickFormatter={(value) => `฿${value.toLocaleString()}`}
              domain={yAxisDomain}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke={Primary}
              fillOpacity={1}
              fill="url(#colorValue)"
              // dot={{ stroke: Primary, strokeWidth: 2, r: 4, fill: White }}
              // activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={Primary}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={200}
        >
          <Typography variant="body2" color={DarkGrey}>
            ไม่มีข้อมูล
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SalesChart;
